/**
 * DAppWebView
 *
 * Native mobile: opens a real InAppBrowser WebView with JavaScript injection.
 * Web/dev: keeps the iframe fallback for quick local iteration.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { Capacitor } from '@capacitor/core'
import { BackgroundColor, InAppBrowser, ToolBarType } from '@capgo/inappbrowser'
import {
  buildInjectionScript,
  validateBridgeMessage,
  type BridgeMessage,
} from '@properwallet/dapp-browser'
import type { TransactionType } from '@wharfkit/antelope'
import type { TransactArgs } from '@wharfkit/session'
import { EOS_CHAIN_ID, FEATURED_DAPPS } from '@properwallet/chain-eos'
import { useWalletStore } from '../store/wallet.store'
import { useTranslation } from '../hooks/useTranslation'
import { decodeTxActions, extractTxActions, type ParsedTxAction } from '../lib/txActions'
import { buildNativeTxConfirmMessage } from '../lib/nativeTxConfirm'
import { nativeConfirm } from '../lib/nativeDialog'
import PinInput, { PIN_LENGTH } from './PinInput'
import Logo from './Logo'
import TxConfirmModal from './TxConfirmModal'

interface DAppWebViewProps {
  url: string
  onClose: () => void
}

type BridgeReply = (result: unknown) => void | Promise<void>
type BridgeReplyError = (error: string) => void | Promise<void>
type DappResponseSender = (message: Record<string, unknown>) => void | Promise<void>

type ProperWalletRequestMessage =
  | { type: 'properwallet:ready'; requestId?: string; reason?: string }
  | { type: 'properwallet:request-auth'; requestId?: string; reason?: string }
  | { type: 'properwallet:request-session'; requestId?: string; reason?: string }
  | { type: 'properwallet:sign-transaction'; requestId: string; args?: unknown; transaction?: unknown }
  | { type: 'properwallet:transact'; requestId: string; args: unknown }

type PendingProperWalletRequest =
  | {
      kind: 'session'
      requestId: string
      reason: string
      send: DappResponseSender
    }
  | {
      kind: 'sign'
      requestId: string
      args: unknown
      send: DappResponseSender
    }
  | {
      kind: 'transact'
      requestId: string
      args: unknown
      send: DappResponseSender
    }

// -- Helpers -----------------------------------------------------------------

const allowedOrigins = new Set<string>(
  FEATURED_DAPPS.map(d => {
    try { return new URL(d.url).hostname } catch { return '' }
  }).filter(Boolean),
)
allowedOrigins.add('null')
allowedOrigins.add('webdex.space')
allowedOrigins.add('www.webdex.space')

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

function hostnameOf(pageUrl: string): string {
  try { return new URL(pageUrl).hostname.toLowerCase() } catch { return '' }
}

function isBridgeHostAllowed(host: string, openedHost: string): boolean {
  if (!host) return false
  if (allowedOrigins.has(host)) return true
  if (host === openedHost) return true
  if (host === 'localhost' || host === '127.0.0.1') return true
  return false
}

function isAllowedOrigin(origin: string): boolean {
  if (origin === 'null') return true
  if (origin.startsWith('capacitor://')) return true
  if (origin.startsWith('https://localhost') || origin.startsWith('http://localhost')) return true
  try { return allowedOrigins.has(new URL(origin).hostname) } catch { return false }
}

function isWebdexUrl(pageUrl: string): boolean {
  try {
    const parsed = new URL(pageUrl)
    const host = parsed.hostname.toLowerCase()
    if (host === 'webdex.space' || host === 'www.webdex.space') return true
    if ((host === 'localhost' || host === '127.0.0.1' || host === '::1') && parsed.port === '3002') {
      return true
    }
    return false
  } catch {
    return false
  }
}

function pageOrigin(pageUrl: string): string {
  try { return new URL(pageUrl).origin } catch {
    return typeof window !== 'undefined' ? window.location.origin : '*'
  }
}

function parseBridgeCandidate(candidate: unknown): BridgeMessage | null {
  let incoming = candidate
  if (typeof incoming === 'string') {
    try { incoming = JSON.parse(incoming) as unknown } catch { return null }
  }
  return validateBridgeMessage(incoming) ? incoming : null
}

function isProperWalletRequestMessage(value: unknown): value is ProperWalletRequestMessage {
  if (!isRecord(value) || typeof value.type !== 'string') return false

  if (
    value.type === 'properwallet:ready' ||
    value.type === 'properwallet:request-auth' ||
    value.type === 'properwallet:request-session'
  ) {
    return typeof value.requestId === 'undefined' || typeof value.requestId === 'string'
  }

  if (value.type === 'properwallet:sign-transaction') {
    return typeof value.requestId === 'string' && value.requestId.length > 0 && ('args' in value || 'transaction' in value)
  }

  if (value.type === 'properwallet:transact') {
    return typeof value.requestId === 'string' && value.requestId.length > 0 && 'args' in value
  }

  return false
}

function parseProperWalletCandidate(candidate: unknown): ProperWalletRequestMessage | null {
  let incoming = candidate
  if (typeof incoming === 'string') {
    try { incoming = JSON.parse(incoming) as unknown } catch { return null }
  }
  return isProperWalletRequestMessage(incoming) ? incoming : null
}

function extractNativeBridgeMessage(detail: Record<string, unknown>): BridgeMessage | null {
  const candidates: unknown[] = [
    detail,
    detail.detail,
    detail.message,
    detail.data,
  ]

  if (isRecord(detail.detail)) {
    candidates.push(detail.detail.detail)
    candidates.push(detail.detail.message)
    candidates.push(detail.detail.data)
  }

  for (const candidate of candidates) {
    const msg = parseBridgeCandidate(candidate)
    if (msg) return msg
  }
  return null
}

function extractNativeProperWalletMessage(detail: Record<string, unknown>): ProperWalletRequestMessage | null {
  const candidates: unknown[] = [
    detail,
    detail.detail,
    detail.message,
    detail.data,
  ]

  if (isRecord(detail.detail)) {
    candidates.push(detail.detail.detail)
    candidates.push(detail.detail.message)
    candidates.push(detail.detail.data)
  }

  for (const candidate of candidates) {
    const msg = parseProperWalletCandidate(candidate)
    if (msg) return msg
  }
  return null
}

function jsValue(value: unknown): string {
  return JSON.stringify(value === undefined ? null : value)
}

function buildNativeBootstrapScript(accountName: string, parentOrigin: string): string {
  if (!accountName) return ''
  return buildInjectionScript(accountName, EOS_CHAIN_ID, parentOrigin)
}

function postToMessageSource(
  source: MessageEventSource | null,
  message: Record<string, unknown>,
  targetOrigin: string,
): void {
  if (!source || !('postMessage' in source)) return
  ;(source as Window).postMessage(message, targetOrigin)
}

function toRequestId(value: string | undefined, fallback: string): string {
  return value && value.length > 0 ? value : fallback
}

// -- Component ---------------------------------------------------------------

export default function DAppWebView({ url, onClose }: DAppWebViewProps) {
  const { activeAccount, activeSession, unlockWithPin } = useWalletStore()
  const { t } = useTranslation()
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const activeSessionRef = useRef(activeSession)
  const accountNameRef = useRef(activeAccount?.name ?? '')
  const pendingProperWalletRequestsRef = useRef<PendingProperWalletRequest[]>([])
  const nativeCurrentUrlRef = useRef(url)
  const openedHostRef = useRef(hostnameOf(url))
  const iframeTxConfirmResolverRef = useRef<((ok: boolean) => void) | null>(null)

  const [loading, setLoading] = useState(true)
  const [dappUnlockRequested, setDappUnlockRequested] = useState(false)
  const [dappUnlockPin, setDappUnlockPin] = useState('')
  const [dappUnlockError, setDappUnlockError] = useState('')
  const [dappUnlockBusy, setDappUnlockBusy] = useState(false)
  const [txConfirm, setTxConfirm] = useState<{ actions: ParsedTxAction[]; broadcast: boolean } | null>(null)
  const [txConfirmBusy, setTxConfirmBusy] = useState(false)

  const accountName = activeAccount?.name ?? ''
  const dappOrigin = pageOrigin(url)
  const isNativeBrowser = Capacitor.isNativePlatform()

  useEffect(() => {
    activeSessionRef.current = activeSession
  }, [activeSession])

  useEffect(() => {
    accountNameRef.current = accountName
  }, [accountName])

  useEffect(() => {
    nativeCurrentUrlRef.current = url
    openedHostRef.current = hostnameOf(url)
  }, [url])

  useEffect(() => {
    console.log('[ProperWallet DAppWebView] opened', {
      url,
      dappOrigin,
      isNativeBrowser,
      isWebdex: isWebdexUrl(url),
      accountName,
      hasActiveSession: !!activeSession,
    })
  }, [accountName, activeSession, dappOrigin, isNativeBrowser, url])

  const sendProperWalletSession = useCallback(async (
    requestId: string,
    send: DappResponseSender,
    reason: string,
  ) => {
    const actor = accountNameRef.current
    const permission = activeSessionRef.current?.permission?.toString() || 'active'
    if (!actor) {
      await send({
        type: 'properwallet:error',
        requestId,
        error: 'No active ProperWallet account',
      })
      return
    }

    await send({
      type: 'properwallet:session',
      requestId,
      accountName: actor,
      actor,
      permission,
      chainId: EOS_CHAIN_ID,
    })

    console.log('[ProperWallet] sent dApp session', {
      requestId,
      reason,
      actor,
      permission,
      chainId: EOS_CHAIN_ID,
    })
  }, [])

  const requestUserTxConfirm = useCallback(async (
    args: unknown,
    broadcast: boolean,
  ): Promise<boolean> => {
    // Decode hex action data (dApp-resolved transactions) so the dialog shows
    // amounts and recipients instead of `{}`. Bounded by a timeout inside.
    const actions = await decodeTxActions(extractTxActions(args))

    if (Capacitor.isNativePlatform()) {
      // SECURITY: confirm via a NATIVE OS dialog, never an overlay injected into
      // the dApp WebView. A button rendered inside the dApp page can be clicked
      // or its handler intercepted by the dApp's own JS, forging approval. The
      // native dialog is drawn outside the WebView and is unreachable from page
      // scripts. Any failure to present it is treated as a rejection.
      try {
        return await nativeConfirm({
          title: t('dapp.confirmTitle'),
          message: buildNativeTxConfirmMessage(actions, { broadcast }),
          okButtonTitle: t('dapp.approve'),
          cancelButtonTitle: t('dapp.reject'),
        })
      } catch (err) {
        console.warn('[ProperWallet] native tx confirm dialog failed (rejecting):', err)
        return false
      }
    }

    return new Promise<boolean>(resolve => {
      iframeTxConfirmResolverRef.current = resolve
      setTxConfirmBusy(false)
      setTxConfirm({ actions, broadcast })
    })
  }, [t])

  const resolveIframeTxConfirm = useCallback((approved: boolean) => {
    const resolve = iframeTxConfirmResolverRef.current
    iframeTxConfirmResolverRef.current = null
    setTxConfirm(null)
    setTxConfirmBusy(false)
    resolve?.(approved)
  }, [])

  const sendProperWalletSignatures = useCallback(async (
    requestId: string,
    transaction: unknown,
    send: DappResponseSender,
  ) => {
    const session = activeSessionRef.current
    if (!session) {
      await send({
        type: 'properwallet:error',
        requestId,
        error: 'Wallet locked',
      })
      return
    }

    const approved = await requestUserTxConfirm(
      { transaction },
      false,
    )
    if (!approved) {
      await send({
        type: 'properwallet:error',
        requestId,
        error: 'Transaction rejected by user',
      })
      return
    }

    try {
      console.log('[ProperWallet] signing dApp transaction', { requestId })
      const signatures = await session.signTransaction(transaction as TransactionType)
      await send({
        type: 'properwallet:signatures',
        requestId,
        signatures: signatures.map(signature => signature.toString()),
      })
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err)
      console.error('[ProperWallet] dApp signing failed:', err)
      await send({
        type: 'properwallet:error',
        requestId,
        error: message,
      })
    }
  }, [requestUserTxConfirm])

  const sendProperWalletSignResult = useCallback(async (
    requestId: string,
    args: unknown,
    send: DappResponseSender,
  ) => {
    console.log('[ProperWallet] sendProperWalletSignResult called', { requestId })

    const session = activeSessionRef.current
    if (!session) {
      await send({
        type: 'properwallet:error',
        requestId,
        error: 'Wallet locked',
      })
      return
    }

    if (!isRecord(args)) {
      await send({
        type: 'properwallet:error',
        requestId,
        error: 'Invalid sign args',
      })
      return
    }

    // The dApp already built and resolved the exact transaction it will
    // broadcast itself (WebDEX's WalletPluginProperWallet.sign() sends
    // `{ transaction }` and only wants signatures back). That transaction must
    // be signed AS-IS. Running it through session.transact() — even with
    // broadcast:false — lets our transact plugins rewrite it (Greymass Fuel
    // prepends a cosign noop action + its own signature, autocorrect may add a
    // PowerUp), so the signatures cover a DIFFERENT digest than the one the
    // dApp broadcasts. The chain then "recovers" random keys and rejects with
    // "transaction declares authority ... but does not have signatures for it".
    if ('transaction' in args && !('actions' in args) && !('action' in args)) {
      await sendProperWalletSignatures(requestId, args.transaction, send)
      return
    }

    const approved = await requestUserTxConfirm(args, false)
    if (!approved) {
      await send({ type: 'properwallet:error', requestId, error: 'Transaction rejected by user' })
      return
    }

    // Tell the injected bridge we're still working (PIN was entered,
    // session.transact() is starting) so it resets its 60s client-side
    // timeout instead of expiring mid-flight. See LEARNINGS.md re:
    // "ProperWallet request timed out" despite the sign actually succeeding.
    await send({ type: 'properwallet:pending', requestId })

    try {
      console.log('[ProperWallet] calling session.transact with broadcast:false', { requestId })
      const result = await session.transact(args as TransactArgs, { broadcast: false })
      const signatures = result.signatures.map(s => String(s))
      // Two consumers listen for this reply and they speak different dialects:
      //  - our injected bridge (packages/dapp-browser/src/injection.ts) waits for
      //    `properwallet:sign-result` with `result.signatures`;
      //  - WebDEX's own ProperWalletAuthBridge.ts only settles on
      //    `properwallet:signatures` with top-level `signatures`.
      // Sending only sign-result meant WebDEX *saw* the reply, ignored it, and
      // then failed the swap 60s later with "ProperWallet request timed out"
      // even though the wallet had signed. Each side ignores the other's type,
      // so sending both is safe.
      await send({
        type: 'properwallet:sign-result',
        requestId,
        result: { signatures },
      })
      await send({
        type: 'properwallet:signatures',
        requestId,
        signatures,
      })
      console.log('[ProperWallet] dApp sign completed', { requestId })
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err)
      console.error('[ProperWallet] sign error:', err)
      await send({
        type: 'properwallet:error',
        requestId,
        error: message,
      })
    }
  }, [requestUserTxConfirm, sendProperWalletSignatures])

  const sendProperWalletTransactResult = useCallback(async (
    requestId: string,
    args: unknown,
    send: DappResponseSender,
  ) => {
    const session = activeSessionRef.current
    if (!session) {
      await send({
        type: 'properwallet:error',
        requestId,
        error: 'Wallet locked',
      })
      return
    }

    if (!isRecord(args)) {
      await send({
        type: 'properwallet:error',
        requestId,
        error: 'Invalid transact args',
      })
      return
    }

    const approved = await requestUserTxConfirm(args, true)
    if (!approved) {
      await send({ type: 'properwallet:error', requestId, error: 'Transaction rejected by user' })
      return
    }

    // See sendProperWalletSignResult above: reset the injected bridge's
    // 60s timeout now that PIN entry is done and session.transact() begins.
    await send({ type: 'properwallet:pending', requestId })

    try {
      console.log('[ProperWallet] executing dApp transact', { requestId })
      const result = await session.transact(args as TransactArgs)
      await send({
        type: 'properwallet:transact-result',
        requestId,
        result: result.response,
      })
      console.log('[ProperWallet] dApp transact completed', { requestId })
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err)
      console.error('[ProperWallet] dApp transact failed:', err)
      await send({
        type: 'properwallet:error',
        requestId,
        error: message,
      })
    }
  }, [requestUserTxConfirm])

  const flushPendingProperWalletRequests = useCallback(() => {
    if (!activeSessionRef.current) return
    const pending = pendingProperWalletRequestsRef.current.splice(0)
    if (pending.length === 0) return

    console.log('[ProperWallet] flushing pending dApp requests', { count: pending.length })
    setDappUnlockRequested(false)
    setDappUnlockPin('')
    setDappUnlockError('')

    for (const request of pending) {
      if (request.kind === 'session') {
        void sendProperWalletSession(request.requestId, request.send, request.reason)
      } else if (request.kind === 'sign') {
        void sendProperWalletSignResult(request.requestId, request.args, request.send)
      } else {
        void sendProperWalletTransactResult(request.requestId, request.args, request.send)
      }
    }
  }, [sendProperWalletSession, sendProperWalletSignResult, sendProperWalletTransactResult])

  useEffect(() => {
    activeSessionRef.current = activeSession
    flushPendingProperWalletRequests()
  }, [activeSession, flushPendingProperWalletRequests])

  const requestDappUnlock = useCallback((reason: string) => {
    if (activeSessionRef.current) return
    console.warn('[ProperWallet] dApp request needs wallet unlock:', {
      reason,
      accountName: accountNameRef.current,
      isWebdex: isWebdexUrl(url),
    })
    setDappUnlockRequested(true)
  }, [url])

  const queueProperWalletRequest = useCallback((request: PendingProperWalletRequest) => {
    pendingProperWalletRequestsRef.current.push(request)
    requestDappUnlock(request.kind === 'session' ? request.reason : request.kind)
  }, [requestDappUnlock])

  const handleProperWalletRequest = useCallback((
    msg: ProperWalletRequestMessage,
    send: DappResponseSender,
  ) => {
    if (msg.type === 'properwallet:ready') {
      console.log('[ProperWallet] dApp ProperWallet bridge is ready', {
        reason: msg.reason,
      })
      return
    }

    if (msg.type === 'properwallet:request-auth' || msg.type === 'properwallet:request-session') {
      const requestId = toRequestId(msg.requestId, `session-${Date.now()}`)
      const reason = msg.reason || msg.type
      void sendProperWalletSession(requestId, send, reason)
      return
    }

    if (msg.type === 'properwallet:sign-transaction') {
      console.log('[ProperWallet] handling sign-transaction', {
        requestId: msg.requestId,
        hasSession: !!activeSessionRef.current
      })
      const signArgs = 'args' in msg ? msg.args :
                       'transaction' in msg ? { transaction: msg.transaction } :
                       msg
      if (!activeSessionRef.current) {
        queueProperWalletRequest({
          kind: 'sign',
          requestId: msg.requestId,
          args: signArgs,
          send,
        })
        return
      }

      void sendProperWalletSignResult(msg.requestId, signArgs, send)
      return
    }

    if (msg.type === 'properwallet:transact') {
      if (!activeSessionRef.current) {
        queueProperWalletRequest({
          kind: 'transact',
          requestId: msg.requestId,
          args: msg.args,
          send,
        })
        return
      }

      void sendProperWalletTransactResult(msg.requestId, msg.args, send)
    }
  }, [queueProperWalletRequest, sendProperWalletSession, sendProperWalletSignResult, sendProperWalletTransactResult])

  const rejectPendingProperWalletRequests = useCallback((error: string) => {
    const pending = pendingProperWalletRequestsRef.current.splice(0)
    for (const request of pending) {
      void request.send({
        type: 'properwallet:error',
        requestId: request.requestId,
        error,
      })
    }
  }, [])

  const submitDappUnlock = useCallback(async (code: string) => {
    if (code.length !== PIN_LENGTH || dappUnlockBusy) return
    setDappUnlockBusy(true)
    setDappUnlockError('')
    try {
      console.log('[ProperWallet] unlocking wallet for dApp request')
      await unlockWithPin(code)
    } catch (err) {
      setDappUnlockPin('')
      setDappUnlockError(err instanceof Error ? err.message : t('pin.wrong'))
      console.warn('[ProperWallet] dApp unlock failed:', err)
    } finally {
      setDappUnlockBusy(false)
    }
  }, [dappUnlockBusy, t, unlockWithPin])

  const onDappUnlockPinChange = useCallback((next: string) => {
    setDappUnlockError('')
    setDappUnlockPin(next)
    if (next.length === PIN_LENGTH) {
      void submitDappUnlock(next)
    }
  }, [submitDappUnlock])

  const handleBridgeRequest = useCallback(async (
    msg: BridgeMessage,
    reply: BridgeReply,
    replyError: BridgeReplyError,
  ) => {
    const { type, payload } = msg

    try {
      if (type === 'ready') {
        await reply({ ok: true })
        return
      }

      if (type === 'getChainId') {
        await reply(EOS_CHAIN_ID)
        return
      }

      if (type === 'getAccount') {
        if (payload.op === 'getIdentity') {
          await reply({
            accounts: [{ name: accountName, authority: 'active', blockchain: 'eos' }],
          })
        } else {
          await reply(accountName)
        }
        return
      }

      if (type === 'transaction' || type === 'pushTransaction' || type === 'pushEosAction') {
        if (payload.op === 'signArbitrary') {
          await replyError('Arbitrary signing not supported')
          return
        }
        if (!activeSession) throw new Error('Wallet locked - unlock ProperWallet first')
        const { op: _op, ...rest } = payload
        const approved = await requestUserTxConfirm(rest, true)
        if (!approved) {
          await replyError('Transaction rejected by user')
          return
        }
        const result = await activeSession.transact(rest as TransactArgs)
        await reply(result.response)
        return
      }

      if (type === 'sign') {
        await replyError('Arbitrary signing not supported')
      }
    } catch (err) {
      await replyError(err instanceof Error ? err.message : String(err))
    }
  }, [activeSession, accountName, requestUserTxConfirm])

  // -- Native WebView ---------------------------------------------------------

  // Keep the latest callbacks in refs so the WebView-opening effect below
  // doesn't tear down and re-open the native browser just because these
  // callbacks got new identities (e.g. activeSession changing after a PIN
  // unlock, or the parent re-rendering with a fresh onClose closure).
  // Re-opening the browser on every such change caused a race where the
  // native "closeEvent" fired by the *old* programmatic close() landed on
  // the *newly* re-registered listener and was mistaken for the user
  // closing the dApp, kicking them back to Discovery mid-transaction.
  const onCloseRef = useRef(onClose)
  useEffect(() => { onCloseRef.current = onClose }, [onClose])

  const handleBridgeRequestRef = useRef(handleBridgeRequest)
  useEffect(() => { handleBridgeRequestRef.current = handleBridgeRequest }, [handleBridgeRequest])

  const handleProperWalletRequestRef = useRef(handleProperWalletRequest)
  useEffect(() => { handleProperWalletRequestRef.current = handleProperWalletRequest }, [handleProperWalletRequest])

  useEffect(() => {
    if (!isNativeBrowser) return

    let cancelled = false

    const resolveNative = async (id: number, result: unknown) => {
      await InAppBrowser.executeScript({
        code: `window.__pw_resolve && window.__pw_resolve(${id}, ${jsValue(result)});`,
      })
    }

    const rejectNative = async (id: number, error: string) => {
      await InAppBrowser.executeScript({
        code: `window.__pw_reject && window.__pw_reject(${id}, ${jsValue(error)});`,
      })
    }

    const dispatchNativeMessage = async (message: Record<string, unknown>) => {
      await InAppBrowser.executeScript({
        code: `window.dispatchEvent(new MessageEvent('message', { data: ${jsValue(message)}, origin: 'capacitor://localhost' }));`,
      })
    }

    const openNativeWebView = async () => {
      setLoading(true)
      await InAppBrowser.removeAllListeners()

      await InAppBrowser.addListener('browserPageLoaded', () => {
        setLoading(false)
        const bootstrap = buildNativeBootstrapScript(accountName, 'capacitor://localhost')
        if (!bootstrap) return
        InAppBrowser.executeScript({ code: bootstrap })
          .catch(err => console.warn('[DAppWebView] native inject failed:', err))
      })

      try {
        await InAppBrowser.addListener('urlChangeEvent' as any, (ev: any) => {
          const next =
            typeof ev?.url === 'string'
              ? ev.url
              : typeof ev?.detail?.url === 'string'
                ? ev.detail.url
                : null
          if (next) nativeCurrentUrlRef.current = next
        })
      } catch {
        // urlChangeEvent unsupported on this platform/plugin version
      }

      await InAppBrowser.addListener('messageFromWebview', event => {
        const detail: Record<string, unknown> = isRecord(event.detail)
          ? event.detail
          : { data: event.detail }

        const currentHost = hostnameOf(nativeCurrentUrlRef.current)
        if (!isBridgeHostAllowed(currentHost, openedHostRef.current)) {
          console.warn('[ProperWallet] bridge: blocked native message from host', currentHost)
          return
        }

        const properWalletMsg = extractNativeProperWalletMessage(detail)
        if (properWalletMsg) {
          console.log('[ProperWallet] got native ProperWallet bridge event from dApp', {
            type: properWalletMsg.type,
            requestId: 'requestId' in properWalletMsg ? properWalletMsg.requestId : undefined,
          })
          handleProperWalletRequestRef.current(properWalletMsg, dispatchNativeMessage)
          return
        }

        const msg = extractNativeBridgeMessage(detail)
        if (!msg) return
        void handleBridgeRequestRef.current(
          msg,
          result => resolveNative(msg.id, result),
          error => rejectNative(msg.id, error),
        )
      })

      await InAppBrowser.addListener('closeEvent', () => {
        if (!cancelled) onCloseRef.current()
      })

      if (cancelled) return

      const preShowScript = buildNativeBootstrapScript(accountName, 'capacitor://localhost')

      await InAppBrowser.openWebView({
        url,
        title: 'ProperWallet',
        toolbarType: ToolBarType.NAVIGATION,
        backgroundColor: BackgroundColor.BLACK,
        toolbarColor: '#15151f',
        toolbarTextColor: '#ffffff',
        visibleTitle: true,
        showReloadButton: true,
        activeNativeNavigationForWebview: true,
        isPresentAfterPageLoad: true,
        preShowScript,
      })
    }

    openNativeWebView()
      .catch(err => {
        console.error('[DAppWebView] native open failed:', err)
        setLoading(false)
      })

    return () => {
      cancelled = true
      void InAppBrowser.removeAllListeners()
      void InAppBrowser.close().catch(() => undefined)
    }
    // Intentionally NOT depending on handleBridgeRequest/handleProperWalletRequest/onClose:
    // those are read via refs above. Depending on them directly caused this effect to
    // tear down and re-open the native WebView on every activeSession change (e.g. every
    // PIN unlock) or parent re-render, racing the native closeEvent and spuriously
    // closing the freshly re-opened browser. Only a real account/url/platform change
    // should reopen the browser.
  }, [accountName, isNativeBrowser, url])

  // -- Provider injection for iframe fallback ---------------------------------

  const injectProviders = useCallback(() => {
    const iframe = iframeRef.current
    if (!iframe?.contentWindow) return

    const script = buildInjectionScript(accountName, EOS_CHAIN_ID, window.location.origin)

    try {
      const doc = iframe.contentDocument
      if (doc?.documentElement) {
        const s = doc.createElement('script')
        s.textContent = script
        doc.documentElement.appendChild(s)
        return
      }
    } catch {
      // Cross-origin iframe.
    }

    iframe.contentWindow.postMessage(
      { __pw_inject: true, accountName, chainId: EOS_CHAIN_ID },
      dappOrigin,
    )
  }, [accountName, dappOrigin])

  const handleLoad = useCallback(() => {
    setLoading(false)
    console.log('[ProperWallet] dApp iframe loaded', {
      url,
      dappOrigin,
      isWebdex: isWebdexUrl(url),
      accountName,
      hasActiveSession: !!activeSessionRef.current,
    })
    injectProviders()
  }, [accountName, dappOrigin, injectProviders, url])

  // -- Iframe bridge ----------------------------------------------------------

  useEffect(() => {
    if (isNativeBrowser) return

    const handleMessage = (event: MessageEvent) => {
      if (event.source !== iframeRef.current?.contentWindow) return
      if (!isAllowedOrigin(event.origin)) {
        console.warn('[ProperWallet] bridge: ignored message from', event.origin)
        return
      }

      if (isRecord(event.data) && typeof event.data.type === 'string' && event.data.type.startsWith('properwallet:')) {
        console.log('[ProperWallet] got ProperWallet bridge event from dApp', {
          origin: event.origin,
          type: event.data.type,
          requestId: typeof event.data.requestId === 'string' ? event.data.requestId : undefined,
        })
      }

      const properWalletMsg = parseProperWalletCandidate(event.data)
      if (properWalletMsg) {
        const replyOrigin = event.origin
        handleProperWalletRequest(
          properWalletMsg,
          message => postToMessageSource(event.source, message, replyOrigin),
        )
        return
      }

      const msg = parseBridgeCandidate(event.data)
      if (!msg) return

      const { id } = msg
      const replyOrigin = event.origin
      const reply = (result: unknown) =>
        postToMessageSource(event.source, { __pw_response: true, id, result }, replyOrigin)
      const replyError = (error: string) =>
        postToMessageSource(event.source, { __pw_response: true, id, error }, replyOrigin)

      void handleBridgeRequest(msg, reply, replyError)
    }

    window.addEventListener('message', handleMessage)
    return () => window.removeEventListener('message', handleMessage)
  }, [handleBridgeRequest, handleProperWalletRequest, isNativeBrowser])

  const closeNative = useCallback(() => {
    void InAppBrowser.close().catch(() => undefined)
    onClose()
  }, [onClose])

  const cancelDappUnlock = useCallback(() => {
    rejectPendingProperWalletRequests('Wallet unlock canceled')
    setDappUnlockRequested(false)
    setDappUnlockPin('')
    setDappUnlockError('')
  }, [rejectPendingProperWalletRequests])

  const dappUnlockOverlay = dappUnlockRequested ? (
    <div className="absolute inset-0 z-30 flex items-center justify-center bg-black/80 px-4">
      <div className="w-full max-w-sm rounded-2xl border border-surface-3 bg-surface-1 p-5 shadow-2xl">
        <h2 className="text-lg font-semibold text-white text-center mb-2">
          {t('pin.enter')}
        </h2>
        <p className="text-sm text-gray-400 text-center mb-6">
          This dApp requests wallet authorization for {accountName}
        </p>
        <PinInput
          value={dappUnlockPin}
          onChange={onDappUnlockPinChange}
          disabled={dappUnlockBusy}
        />
        {dappUnlockError ? (
          <p className="text-danger text-sm text-center mt-4">{dappUnlockError}</p>
        ) : null}
        <button
          type="button"
          className="mt-5 text-sm text-gray-400 w-full text-center"
          onClick={cancelDappUnlock}
        >
          {t('common.cancel')}
        </button>
      </div>
    </div>
  ) : null

  const txConfirmOverlay = txConfirm ? (
    <TxConfirmModal
      actions={txConfirm.actions}
      broadcast={txConfirm.broadcast}
      dappUrl={url}
      accountName={accountName}
      busy={txConfirmBusy}
      onApprove={() => {
        setTxConfirmBusy(true)
        resolveIframeTxConfirm(true)
      }}
      onReject={() => {
        setTxConfirmBusy(true)
        resolveIframeTxConfirm(false)
      }}
      labels={{
        title: t('dapp.confirmTitle'),
        subtitle: t('dapp.confirmSubtitle'),
        broadcastNote: t('dapp.broadcastNote'),
        signOnlyNote: t('dapp.signOnlyNote'),
        approve: t('dapp.approve'),
        reject: t('dapp.reject'),
        noActions: t('dapp.noActions'),
      }}
    />
  ) : null

  if (isNativeBrowser) {
    return (
      <div className="relative flex flex-col h-full bg-surface animate-fade-in">
        <div className="bg-surface-1 border-b border-surface-3 px-3 py-2 flex items-center gap-2 flex-shrink-0 pt-safe">
          <button
            onClick={closeNative}
            className="text-gray-400 hover:text-white p-1 text-sm flex-shrink-0"
          >
            x
          </button>
          <Logo size="sm" className="flex-shrink-0" />
          <div className="flex-1 bg-surface-2 rounded-lg px-3 py-1.5 text-xs text-gray-300 truncate">
            {url}
          </div>
          {accountName && (
            <div className="text-xs text-brand-light bg-brand/10 px-2 py-1 rounded-lg flex-shrink-0 max-w-[90px] truncate">
              {accountName}
            </div>
          )}
        </div>

        <div className="flex-1 flex items-center justify-center">
          {loading && (
            <div className="flex flex-col items-center gap-3">
              <div className="w-8 h-8 border-2 border-brand border-t-transparent rounded-full animate-spin" />
              <p className="text-gray-400 text-sm">{t('common.loading')}</p>
            </div>
          )}
        </div>
        {dappUnlockOverlay}
        {txConfirmOverlay}
      </div>
    )
  }

  return (
    <div className="relative flex flex-col h-full bg-surface animate-fade-in">
      <div className="bg-surface-1 border-b border-surface-3 px-3 py-2 flex items-center gap-2 flex-shrink-0 pt-safe">
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-white p-1 text-sm flex-shrink-0"
        >
          x
        </button>
        <Logo size="sm" className="flex-shrink-0" />
        <div className="flex-1 bg-surface-2 rounded-lg px-3 py-1.5 text-xs text-gray-300 truncate">
          {url}
        </div>
        {accountName && (
          <div className="text-xs text-brand-light bg-brand/10 px-2 py-1 rounded-lg flex-shrink-0 max-w-[90px] truncate">
            {accountName}
          </div>
        )}
      </div>

      {loading && (
        <div className="absolute inset-0 flex items-center justify-center bg-surface z-10 mt-12">
          <div className="flex flex-col items-center gap-3">
            <div className="w-8 h-8 border-2 border-brand border-t-transparent rounded-full animate-spin" />
            <p className="text-gray-400 text-sm">{t('common.loading')}</p>
          </div>
        </div>
      )}

      {dappUnlockOverlay}
      {txConfirmOverlay}

      <iframe
        ref={iframeRef}
        src={url}
        className="flex-1 w-full border-0"
        sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox"
        onLoad={handleLoad}
        title="dApp Browser"
      />
    </div>
  )
}
